/**
 * ShopScene — purchase upgrades with gold.
 *
 * Shows 10 random items (attributes, skills, perks, grid tiles).
 * Prices escalate with each purchase across the run.
 * Grid tile purchases open the placement panel before returning.
 */

declare const Phaser: any;

import { BaseScene } from "../../framework/BaseScene";
import { RunState } from "../data/RunState";
import { generateShopItems } from "../shop/ShopInventory";
import { ShopItem } from "../shop/ShopItem";
import { ShapePlacementPanel } from "./battle/grid/ShapePlacementPanel";
import { MONO } from "../character/shapes";

interface ShopSlot {
  item: ShopItem;
  price: number;
  sold: boolean;
  objects: any[];
}

export class ShopScene extends BaseScene {
  private run!: RunState;
  private slots: ShopSlot[] = [];
  private goldText!: any;

  constructor() {
    super({ key: "ShopScene" });
  }

  onReady(_data?: any): void {
    this.run = this.game.registry.get("runState");
    this.slots = [];

    // Background
    this.add.rectangle(400, 225, 800, 450, 0x0f172a);

    // Title
    this.add.text(400, 24, "SHOP", {
      fontFamily: "system-ui, sans-serif",
      fontSize: "22px",
      color: "#eab308",
      fontStyle: "bold",
    }).setOrigin(0.5);

    // Gold display
    this.goldText = this.add.text(400, 48, this.goldLabel(), {
      fontFamily: "system-ui, sans-serif",
      fontSize: "13px",
      color: "#fbbf24",
    }).setOrigin(0.5);

    // Generate items
    const items = generateShopItems(
      this.run.player,
      this.run.shopPurchases,
      10,
    );

    // Layout: 2 columns × 5 rows, centered
    const cols = 2;
    const colW = 340;
    const gap = 10;
    const totalW = cols * colW + gap;
    const rowH = 58;
    const startX = (800 - totalW) / 2;
    const startY = 78;

    for (let i = 0; i < items.length; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (colW + gap);
      const y = startY + row * rowH;

      this.createSlot(items[i].item, items[i].price, x, y, colW, rowH - 6);
    }

    // Leave button
    const leaveY = startY + Math.ceil(items.length / cols) * rowH + 12;
    this.createLeaveButton(400, Math.min(leaveY, 420));
  }

  onUpdate(_time: number, _delta: number): void {}

  private goldLabel(): string {
    return `Gold: ${this.run.player.gold}`;
  }

  private createSlot(
    item: ShopItem,
    price: number,
    x: number, y: number,
    w: number, h: number,
  ): void {
    const objects: any[] = [];
    const cx = x + w / 2;
    const cy = y + h / 2;
    const canAfford = this.run.player.gold >= price;

    // Background
    const bg = this.add.rectangle(cx, cy, w, h, 0x1e293b, 0.9);
    bg.setStrokeStyle(1, 0x334155);
    objects.push(bg);

    // Icon badge
    const badgeSize = 28;
    const badgeX = x + 6 + badgeSize / 2;
    const btnColor = parseInt(item.color.replace("#", ""), 16);
    const badge = this.add.rectangle(badgeX, cy, badgeSize, badgeSize, btnColor, 0.85);
    objects.push(badge);

    const iconText = this.add.text(badgeX, cy, item.icon, {
      fontFamily: "system-ui, sans-serif",
      fontSize: "12px", color: "#ffffff", fontStyle: "bold",
    }).setOrigin(0.5);
    objects.push(iconText);

    // Name
    const textX = badgeX + badgeSize / 2 + 8;
    const nameText = this.add.text(textX, cy - 9, item.name, {
      fontFamily: "system-ui, sans-serif",
      fontSize: "11px", color: "#ffffff", fontStyle: "bold",
    }).setOrigin(0, 0.5);
    objects.push(nameText);

    // Description
    const descText = this.add.text(textX, cy + 7, item.description, {
      fontFamily: "system-ui, sans-serif",
      fontSize: "8px", color: "#9ca3af",
      wordWrap: { width: w - badgeSize - 80 },
    }).setOrigin(0, 0.5);
    objects.push(descText);

    // Price tag (right side)
    const priceX = x + w - 8;
    const priceColor = canAfford ? "#fbbf24" : "#6b7280";
    const priceText = this.add.text(priceX, cy, `${price}g`, {
      fontFamily: "system-ui, sans-serif",
      fontSize: "12px", color: priceColor, fontStyle: "bold",
    }).setOrigin(1, 0.5);
    objects.push(priceText);

    const slot: ShopSlot = { item, price, sold: false, objects };
    this.slots.push(slot);

    // Interactive
    if (canAfford) {
      bg.setInteractive({ useHandCursor: true });
      bg.on("pointerover", () => {
        if (!slot.sold) bg.setFillStyle(0x334155, 0.95);
      });
      bg.on("pointerout", () => {
        if (!slot.sold) bg.setFillStyle(0x1e293b, 0.9);
      });
      bg.on("pointerdown", () => {
        if (slot.sold) return;
        this.purchaseItem(slot);
      });
    } else {
      for (const obj of objects) {
        if (obj.setAlpha) obj.setAlpha(0.4);
      }
    }
  }

  private purchaseItem(slot: ShopSlot): void {
    const player = this.run.player;
    if (!player.spendGold(slot.price)) return;

    slot.sold = true;
    this.run.shopPurchases++;

    // Grid tile — open placement panel
    if (slot.item.gridPlacement) {
      slot.item.apply(player);
      const placement = new ShapePlacementPanel(
        this,
        player.gridMask,
        MONO,
        (updatedMask) => {
          player.gridMask = updatedMask;
          this.markSold(slot);
          this.refreshAffordability();
        },
      );
      placement.show();
      return;
    }

    // Normal item — apply immediately
    slot.item.apply(player);
    this.markSold(slot);
    this.refreshAffordability();
  }

  private markSold(slot: ShopSlot): void {
    this.goldText.setText(this.goldLabel());

    for (const obj of slot.objects) {
      if (obj.setAlpha) obj.setAlpha(0.25);
    }
    const bg = slot.objects[0];
    if (bg) {
      bg.removeInteractive();
      bg.removeAllListeners();
    }

    const soldText = this.add.text(bg.x, bg.y, "SOLD", {
      fontFamily: "system-ui, sans-serif",
      fontSize: "14px", color: "#4ade80", fontStyle: "bold",
    }).setOrigin(0.5);
    slot.objects.push(soldText);
  }

  private refreshAffordability(): void {
    const gold = this.run.player.gold;
    const multiplier = 1 + this.run.shopPurchases * 0.2;

    for (const slot of this.slots) {
      if (slot.sold) continue;

      const newPrice = Math.ceil(slot.item.basePrice * multiplier);
      slot.price = newPrice;

      const canAfford = gold >= newPrice;

      // Update price text
      for (const obj of slot.objects) {
        if (obj.text && typeof obj.text === "string" && obj.text.endsWith("g")) {
          obj.setText(`${newPrice}g`);
          obj.setColor(canAfford ? "#fbbf24" : "#6b7280");
        }
      }

      if (!canAfford) {
        for (const obj of slot.objects) {
          if (obj.setAlpha) obj.setAlpha(0.4);
        }
        const bg = slot.objects[0];
        if (bg) {
          bg.removeInteractive();
          bg.removeAllListeners();
        }
      }
    }
  }

  private createLeaveButton(x: number, y: number): void {
    const btn = this.add.rectangle(x, y, 140, 34, 0x374151);
    btn.setInteractive({ useHandCursor: true });
    btn.on("pointerover", () => btn.setFillStyle(0x475569));
    btn.on("pointerout", () => btn.setFillStyle(0x374151));

    this.add.text(x, y, "LEAVE SHOP", {
      fontFamily: "system-ui, sans-serif",
      fontSize: "12px",
      color: "#e5e7eb",
      fontStyle: "bold",
    }).setOrigin(0.5);

    btn.on("pointerdown", () => {
      this.sm.pop();
    });
  }
}
